import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  addTransaction,
  updateTransaction,
  deleteTransaction,
} from '../redux/transactionSlice';
import { dateToString } from '../shared/formatters';

const emptyData = {
  id: 0,
  description: '',
  value: 0,
  categoryId: '',
  financeAccountId: '',
  date: dateToString(new Date()),
};

/**
 * Validates transaction data
 * @param {Object} item - Transaction data to validate
 * @returns {Object} Validation errors object with field names as keys
 */
const validateTransaction = (item) => {
  const errors = {};

  // Check description is not empty
  if (!item.description || !item.description.toString().trim().length) {
    errors.description = 'Descrição é obrigatória';
  }

  // Check value is greater than 0
  if (!item.value || item.value <= 0) {
    errors.value = 'Valor deve ser maior que 0';
  }

  // Check categoryId is selected
  if (!item.categoryId) {
    errors.categoryId = 'Categoria é obrigatória';
  }

  // Check financeAccountId is selected
  if (!item.financeAccountId) {
    errors.financeAccountId = 'Conta é obrigatória';
  }

  // Check date is valid
  if (!item.date) {
    errors.date = 'Data é obrigatória';
  }

  return errors;
};

/**
 * Custom hook to manage transaction form state and operations
 * @param {Object} initialData - Initial transaction data
 * @returns {Object} Form state and handlers
 */
export const useTransactionForm = (initialData = emptyData) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [item, setItem] = useState({ ...initialData });
  const [errors, setErrors] = useState({});

  const updateItem = useCallback((updates) => {
    setItem((prev) => ({ ...prev, ...updates }));
    // Clear errors for updated fields
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      Object.keys(updates).forEach((key) => {
        delete newErrors[key];
      });
      return newErrors;
    });
  }, []);

  const resetForm = useCallback((data = emptyData) => {
    setItem({ ...data });
    setErrors({});
  }, []);

  const submitForm = useCallback(
    async (event, saveAndAddNew = false) => {
      event.preventDefault();

      // Validate form data
      const validationErrors = validateTransaction(item);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        console.error('Validation errors:', validationErrors);
        return;
      }

      // Clear errors if validation passes
      setErrors({});

      try {
        if (item.id) {
          await dispatch(updateTransaction(item)).unwrap();
          navigate('/transactions');
        } else {
          await dispatch(addTransaction(item)).unwrap();
          if (saveAndAddNew) {
            // Keep category, account, and date for convenience
            setItem({
              ...emptyData,
              categoryId: item.categoryId,
              financeAccountId: item.financeAccountId,
              date: item.date,
              id: 0,
            });
          } else {
            navigate('/transactions');
          }
        }
      } catch (error) {
        console.error('Error submitting transaction:', error);
        throw error;
      }
    },
    [item, dispatch, navigate]
  );

  const deleteAction = useCallback(
    async (id) => {
      try {
        await dispatch(deleteTransaction(id)).unwrap();
        navigate('/transactions');
      } catch (error) {
        console.error('Error deleting transaction:', error);
        throw error;
      }
    },
    [dispatch, navigate]
  );

  const updateFromGemini = useCallback(
    (suggestedItem, categories, accounts) => {
      const category = suggestedItem.category
        ? categories.find(
            (cat) =>
              cat.name.toLowerCase().trim() ===
              suggestedItem.category.toLowerCase().trim()
          )
        : null;

      const account = suggestedItem.account
        ? accounts.find(
            (acc) =>
              acc.name.toLowerCase().trim() ===
              suggestedItem.account.toLowerCase().trim()
          )
        : null;

      const updatedItem = {
        ...item,
        description: suggestedItem.description || '',
        value: suggestedItem.value || 0,
        categoryId: category ? category.id : '',
        financeAccountId: account ? account.id : '',
        date: suggestedItem.date || dateToString(new Date()),
        attachment: suggestedItem.attachment || null,
      };

      setItem(updatedItem);
      return updatedItem;
    },
    [item]
  );

  return {
    item,
    errors,
    updateItem,
    resetForm,
    submitForm,
    deleteAction,
    updateFromGemini,
  };
};
